/**
 * Camera controller: third person (orbit / over-the-shoulder aim), vehicle chase
 * cam with lag, far and cinematic modes, collision against the city, shake.
 *
 * Conventions: yaw is a heading (forward = (sin yaw, 0, cos yaw)); pitch > 0 puts
 * the camera ABOVE the target looking down. right = forward x up = (-cos yaw, 0, sin yaw).
 */
import * as THREE from 'three';
import { G } from './ctx';
import type { CameraMode, ICamera } from './types';
import { clamp, damp, dampAngle, lerp } from './util';

const SENS = 0.0022;
const FOOT_MODES: CameraMode[] = ['third', 'far', 'cinematic'];
const CAR_MODES: CameraMode[] = ['chase', 'far', 'cinematic'];

const _target = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _pos = new THREE.Vector3();
const _look = new THREE.Vector3();
const _right = new THREE.Vector3();
const _fwd = new THREE.Vector3();

export class CameraController implements ICamera {
  mode: CameraMode = 'third';
  yaw = 0;
  pitch = 0.26;

  private inVehicle = false;
  private first = true;
  private lookIdle = 0;
  private aimBlend = 0;
  private offset = new THREE.Vector3();
  private chaseYaw = 0;
  private userYaw = 0;
  private userPitch = 0;
  private userIdle = 0;
  private fov = 62;
  private cineT = 0;
  private shakeT = 0;
  private shakeDur = 0;
  private shakeAmp = 0;
  private raycaster = new THREE.Raycaster();
  private prevMode: CameraMode = 'third';

  constructor() {
    const p = G.player;
    if (p) {
      this.yaw = p.heading;
      this.chaseYaw = p.heading;
    }
    this.raycaster.near = 0.05;
  }

  forward(out: THREE.Vector3): THREE.Vector3 {
    return out.set(Math.sin(this.yaw), 0, Math.cos(this.yaw));
  }

  right(out: THREE.Vector3): THREE.Vector3 {
    return out.set(-Math.cos(this.yaw), 0, Math.sin(this.yaw));
  }

  cycleMode(): void {
    const fam = this.inVehicle ? CAR_MODES : FOOT_MODES;
    let i = fam.indexOf(this.mode);
    i = (i + 1) % fam.length;
    this.mode = fam[i];
    if (this.mode === 'cinematic') this.cineT = 0;
  }

  shake(intensity: number, seconds: number): void {
    if (intensity <= 0 || seconds <= 0) return;
    if (intensity >= this.shakeAmp * (this.shakeT / Math.max(this.shakeDur, 1e-3))) {
      this.shakeAmp = intensity;
      this.shakeDur = seconds;
      this.shakeT = seconds;
    }
  }

  update(dt: number): void {
    const p = G.player;
    const cam = G.camera;
    const inp = G.input;
    const veh = p.vehicle;
    const inVeh = !!veh;

    if (inVeh !== this.inVehicle) {
      this.inVehicle = inVeh;
      if (inVeh) {
        if (this.mode === 'third') this.mode = 'chase';
        this.chaseYaw = veh!.heading;
        this.userYaw = 0;
        this.userPitch = 0;
        this.userIdle = 0;
      } else {
        if (this.mode === 'chase') this.mode = 'third';
        this.yaw = this.chaseYaw + this.userYaw;
        this.pitch = clamp(0.22 + this.userPitch, -0.35, 1.1);
        this.lookIdle = 0;
      }
    }
    if (this.mode !== this.prevMode) {
      this.prevMode = this.mode;
      if (this.mode === 'cinematic') this.cineT = 0;
    }

    const inv = G.hud?.settings?.invertY ? -1 : 1;
    const dx = inp.lookDX;
    const dy = inp.lookDY;
    const hasLook = dx !== 0 || dy !== 0;

    let fovT = 62;
    if (!inVeh) {
      this.yaw -= dx * SENS;
      this.pitch = clamp(this.pitch + dy * SENS * inv, -0.35, 1.1);
      this.lookIdle = hasLook ? 0 : this.lookIdle + dt;
      if (this.lookIdle > 1.5 && p.speed > 0.5 && !p.aiming && this.mode === 'third' && !p.dead) this.yaw = dampAngle(this.yaw, p.heading, 1.3, dt);
      this.aimBlend = damp(this.aimBlend, p.aiming && this.mode === 'third' ? 1 : 0, 12, dt);
      fovT = this.updateOnFoot(dt);
    } else {
      const v = veh!;
      const reversing = v.speed < -3;
      const desired = reversing ? v.heading + Math.PI : v.heading;
      this.chaseYaw = dampAngle(this.chaseYaw, desired, reversing ? 3 : 4, dt);
      this.userYaw -= dx * SENS;
      this.userPitch = clamp(this.userPitch + dy * SENS * inv, -0.35, 0.8);
      this.userIdle = hasLook ? 0 : this.userIdle + dt;
      if (this.userIdle > 2) {
        this.userYaw = dampAngle(this.userYaw, 0, 3, dt);
        this.userPitch = damp(this.userPitch, 0, 3, dt);
      }
      this.yaw = this.chaseYaw + this.userYaw;
      this.pitch = 0.2 + this.userPitch;
      this.aimBlend = 0;
      fovT = this.updateChase(dt, v);
    }

    // shake
    if (this.shakeT > 0) {
      this.shakeT -= dt;
      const k = Math.max(0, this.shakeT / this.shakeDur);
      const amp = this.shakeAmp * k * k;
      _pos.x += (Math.random() - 0.5) * 2 * amp;
      _pos.y += (Math.random() - 0.5) * 2 * amp;
      _pos.z += (Math.random() - 0.5) * 2 * amp * 0.5;
      _look.x += (Math.random() - 0.5) * amp;
      _look.y += (Math.random() - 0.5) * amp;
    }
    if (_pos.y < 0.6) _pos.y = 0.6;
    if (!Number.isFinite(_pos.x) || !Number.isFinite(_pos.y) || !Number.isFinite(_pos.z)) {
      _pos.set(p.pos.x, p.pos.y + 3, p.pos.z - 4);
      _look.copy(p.pos);
    }
    cam.position.copy(_pos);
    cam.lookAt(_look);

    // fov (aim narrows, speed widens)
    this.fov = this.first ? fovT : damp(this.fov, fovT, 8, dt);
    if (Math.abs(cam.fov - this.fov) > 0.01) {
      cam.fov = this.fov;
      cam.updateProjectionMatrix();
    }
    this.first = false;
  }

  /** third / far / cinematic on foot. Fills _pos/_look, returns the target fov. */
  private updateOnFoot(dt: number): number {
    const p = G.player;
    const jumpY = p.group.position.y; // cosmetic jump arc lives on the player group
    const headH = p.dead ? 0.5 : lerp(1.5, 1.32, this.aimBlend);
    _target.set(p.pos.x, headH + jumpY, p.pos.z);
    this.right(_right);
    this.forward(_fwd);

    if (this.mode === 'cinematic') {
      this.cineT += dt;
      const ang = this.cineT * 0.22 + this.yaw;
      _pos.set(_target.x + Math.sin(ang) * 12, _target.y + 3.2 + Math.sin(this.cineT * 0.3) * 0.6, _target.z + Math.cos(ang) * 12);
      this.collide(_target, _pos, 10, dt);
      _look.copy(_target).addScaledVector(_fwd, 0.5);
      return 55;
    }

    const far = this.mode === 'far';
    const ab = this.aimBlend;
    const dist = far ? 7.2 : lerp(3.6, 2.1, ab);
    const side = far ? 0 : 0.65 * ab;
    _target.addScaledVector(_right, side);
    const cp = Math.cos(this.pitch);
    _dir.set(-Math.sin(this.yaw) * cp, Math.sin(this.pitch), -Math.cos(this.yaw) * cp);
    _pos.copy(_target).addScaledVector(_dir, dist);
    this.collide(_target, _pos, far ? 12 : 10, dt);
    // look slightly above the head; when aiming look along the view direction so the crosshair is centred
    _look.copy(_target).addScaledVector(_dir, -6 * ab);
    if (ab < 1) _look.y += 0.12 * (1 - ab);
    return lerp(62, 50, ab);
  }

  /** chase / far / cinematic in a vehicle. */
  private updateChase(dt: number, v: NonNullable<typeof G.player.vehicle>): number {
    const sp = Math.abs(v.speed);
    _target.set(v.pos.x, v.pos.y + 1.2, v.pos.z);
    _fwd.set(Math.sin(v.heading), 0, Math.cos(v.heading));

    if (this.mode === 'cinematic') {
      this.cineT += dt;
      const ang = this.cineT * 0.2 + v.heading + Math.PI * 0.75;
      _pos.set(_target.x + Math.sin(ang) * 13, _target.y + 3.5, _target.z + Math.cos(ang) * 13);
      this.collide(_target, _pos, 10, dt);
      _look.copy(_target).addScaledVector(_fwd, 1);
      return 55;
    }
    const far = this.mode === 'far';
    const back = (7.5 + sp * 0.06) * (far ? 2 : 1);
    const height = (2.6 + sp * 0.01) * (far ? 1.7 : 1);
    // spherical around the target using the chase yaw + user offsets
    const basePitch = Math.atan2(height - 1.2, back);
    const pitch = clamp(basePitch + this.userPitch, -0.2, 1.2);
    const dist = Math.hypot(back, height - 1.2);
    const cp = Math.cos(pitch);
    _dir.set(-Math.sin(this.yaw) * cp, Math.sin(pitch), -Math.cos(this.yaw) * cp);
    _pos.copy(_target).addScaledVector(_dir, dist);
    this.collide(_target, _pos, 12, dt);
    const lead = Math.min(4, 1.2 + sp * 0.06);
    _look.copy(_target).addScaledVector(_fwd, v.speed < -3 ? -lead : lead);
    _look.y += 0.2;
    return 62 + Math.min(10, sp * 0.25);
  }

  /**
   * Pull `pos` toward `target` in front of any city geometry along the segment,
   * then damp the (target-relative) offset for smooth transitions with zero lag.
   */
  private collide(target: THREE.Vector3, pos: THREE.Vector3, lambda: number, dt: number): void {
    _dir.copy(pos).sub(target);
    const want = _dir.length();
    if (want > 1e-4) {
      _dir.multiplyScalar(1 / want);
      const targets = G.city?.raycastTargets;
      if (targets && targets.length) {
        this.raycaster.set(target, _dir);
        this.raycaster.far = want + 0.3;
        const hits = this.raycaster.intersectObjects(targets, true);
        if (hits.length) {
          const d = Math.max(0.45, hits[0].distance - 0.3);
          if (d < want) pos.copy(target).addScaledVector(_dir, d);
        }
      }
    }
    _dir.copy(pos).sub(target); // desired offset
    if (this.first || dt <= 0) {
      this.offset.copy(_dir);
    } else {
      // move in fast (obstacles), move out a little slower (no pop)
      const closer = _dir.lengthSq() < this.offset.lengthSq();
      const lam = closer ? Math.max(lambda, 25) : lambda;
      this.offset.x = damp(this.offset.x, _dir.x, lam, dt);
      this.offset.y = damp(this.offset.y, _dir.y, lam, dt);
      this.offset.z = damp(this.offset.z, _dir.z, lam, dt);
    }
    pos.copy(target).add(this.offset);
    // never let the damped offset sit inside geometry: re-check the damped position quickly
    if (pos.y < 0.6) pos.y = 0.6;
  }
}
